import { html, type TemplateResult } from 'lit';

/*
 * THE STATECHART'S OWN STYLING, FOR WHEREVER THE PAGE STYLESHEET CANNOT REACH.
 *
 * `<vitesse-statechart>` renders into the LIGHT DOM on purpose: on the model page it is styled by
 * the app's global `_statechart-renderer.scss`. Inside the AI transcript it is not on the page — it
 * is inside base-ui's shadow tree, which that stylesheet cannot reach — and the SVG rendered with
 * no fills, no strokes and no text colour: a solid black rectangle.
 *
 * The fix is not to drag the app's stylesheet across the boundary — it is a page stylesheet and
 * pulling it in would bring the whole model page's assumptions with it. This is the small,
 * self-contained set a statechart actually needs, written against the SAME `--sc-*` tokens the full
 * renderer uses. Custom properties inherit THROUGH a shadow boundary, so both themes come out right
 * here for the same reason they do on the page.
 *
 * ONE COPY, TWO HOMES: the models.write work card (ai-tools/model-card.ts) and the replay viewer
 * (components/model/vitesse-statechart-replay.ts) both wrap the chart in `.wc-chart` and emit this.
 * The replay viewer lives on the model page AND inside a work card, so it has to carry its styling
 * with it either way — which is why the set gained the firing-edge and never-entered rules here
 * rather than only in the page stylesheet.
 */
export const STATECHART_INLINE_SCOPE = 'wc-chart';

export const STATECHART_INLINE_STYLES: TemplateResult = html`<style>
  .wc-chart svg {
    width: 100%;
    height: auto;
    display: block;
  }
  .wc-chart .state-rect {
    fill: var(--sc-state-bg-0, var(--bg-secondary));
    stroke: var(--sc-state-border-0, var(--border-color));
    stroke-width: 1;
  }
  .wc-chart .state-rect.level-1 {
    fill: var(--sc-state-bg-1, var(--bg-secondary));
    stroke: var(--sc-state-border-1, var(--border-color));
  }
  .wc-chart .state-rect.level-2 {
    fill: var(--sc-state-bg-2, var(--bg-secondary));
    stroke: var(--sc-state-border-2, var(--border-color));
  }
  .wc-chart .state-rect.level-3 {
    fill: var(--sc-state-bg-3, var(--bg-secondary));
    stroke: var(--sc-state-border-3, var(--border-color));
  }
  .wc-chart .state-label,
  .wc-chart .transition-label {
    fill: var(--text-primary);
    font-family: var(--font, inherit);
    font-size: 12px;
    user-select: none;
  }
  .wc-chart .transition-path {
    fill: none;
    stroke: var(--sc-edge, var(--text-tertiary));
    stroke-width: 1.5;
  }
  .wc-chart .transition-hover-area {
    fill: none;
    stroke: transparent;
  }
  .wc-chart .transition-label-bg {
    fill: var(--bg-secondary);
  }
  .wc-chart .arrow-path,
  .wc-chart .initial-state,
  .wc-chart .final-state-outer,
  .wc-chart .final-state-inner {
    fill: var(--sc-edge, var(--text-tertiary));
  }
  .wc-chart .final-state-inner {
    fill: var(--text-primary);
  }
  /* The hover card is the model page's — a floating detail panel with the page's own styling
     behind it. In a transcript it floated unstyled over the chart; a preview is for looking, and
     the page is one click away for inspecting. */
  .wc-chart .hover-card {
    display: none;
  }
  /* The active state, when the model is running: the one thing worth a colour of its own. */
  .wc-chart .state-rect.active {
    stroke: var(--blue);
    stroke-width: 2;
  }
  .wc-chart .state-label.active {
    fill: var(--blue-light, var(--blue));
  }
  /* The edge that just fired in a replay. Green, like the page's pulse, but steady: in a
     transcript there is no page stylesheet to animate it, and a steady edge scrubs better. */
  .wc-chart .transition-path.active {
    stroke: var(--green);
    stroke-width: 2;
  }
  .wc-chart .transition-label.active {
    fill: var(--green);
  }
  .wc-chart .arrow-path-active {
    fill: var(--green);
  }
  /* A state the replay never reached: dashed and dimmed, with a tag on its top edge. */
  .wc-chart .state-rect.never-entered {
    stroke-dasharray: 4 3;
    opacity: 0.7;
  }
  .wc-chart .never-badge-bg {
    fill: var(--bg-primary, var(--bg-secondary));
    stroke: var(--text-tertiary, var(--border-color));
    stroke-width: 1;
  }
  .wc-chart .never-badge {
    fill: var(--text-secondary);
    font-family: var(--font, inherit);
    font-size: 8px;
    user-select: none;
    pointer-events: none;
  }
</style>`;
